// Links to stored files that expire, read from the link itself (#431, #497).
//
// The rules are `core/src/file_links.rs`, whose header is the argument for the
// numbers — thirty days, renewed with three or fewer left, the expiry rounded
// up to the end of the UTC day — and for reading the expiry out of the URL
// rather than keeping it beside the URL. This file is the web's and the
// functions' adapter over them: it takes JavaScript's milliseconds, which is
// what every clock in `src/` holds, and hands the core the whole seconds it
// asks for.
//
// It is in `src/domain/` because both sides need it — the client to decide
// when to ask for a new link (src/data/fileLinks.js), the functions to decide
// what expiry to mint and whether to honour one (functions/files/links.js) —
// and this directory is the one both of them load.
import { coreSync } from './core.js';

const toSeconds = (ms) => Math.floor(Number(ms) / 1000);

/** When `url` stops working, in epoch milliseconds, or null if it does not say. */
export function linkExpiresAt(url) {
  const seconds = coreSync().fileLinkExpiresAt(String(url ?? ''));
  return seconds == null ? null : seconds * 1000;
}

/**
 * Whether a link must be replaced before it is used at `nowMs`: three days or
 * fewer left, expired, or with no expiry to read — which includes a
 * download-token or public URL, and a six-hour link (#431).
 */
export function linkNeedsRenewal(url, nowMs = Date.now()) {
  return coreSync().fileLinkNeedsRenewal(String(url ?? ''), toSeconds(nowMs));
}

/**
 * The `exp` a link minted at `nowMs` carries, in epoch **seconds** — the unit
 * the URL writes it in, and the only one this file hands back, because it is
 * the one the HMAC is taken over.
 */
export function linkExpirySeconds(nowMs = Date.now()) {
  return coreSync().fileLinkExpiry(toSeconds(nowMs));
}

/** Whether the server honours a correctly signed link whose `exp` is `expSeconds`. */
export function linkIsLive(expSeconds, nowMs = Date.now()) {
  return coreSync().fileLinkIsLive(expSeconds, toSeconds(nowMs));
}

/**
 * What the redirect's V4 URL is signed with at `nowMs`: `accessibleAt` (epoch
 * milliseconds, the start of the current ten minutes) and `expiresInSeconds`.
 */
export function redirectWindow(nowMs = Date.now()) {
  const w = coreSync().fileLinkRedirectWindow(toSeconds(nowMs));
  return { accessibleAt: w.accessibleAt * 1000, expiresInSeconds: w.expiresIn };
}

/** How many seconds a browser may keep the redirect itself. */
export function redirectCacheSeconds(nowMs = Date.now()) {
  return coreSync().fileLinkRedirectCacheSeconds(toSeconds(nowMs));
}

/** The lifetime, window and renewal margin, and the redirect's two, in seconds. */
export function linkPolicy() {
  const p = coreSync().fileLinkPolicy();
  return {
    lifetimeSeconds: p.lifetimeSeconds,
    windowSeconds: p.windowSeconds,
    renewMarginSeconds: p.renewMarginSeconds,
    redirectLifetimeSeconds: p.redirectLifetimeSeconds,
    redirectWindowSeconds: p.redirectWindowSeconds,
  };
}

/**
 * A sentence about file links, by code — `app_note`, `email_note`,
 * `sms_note` and the pages an opened link can land on. The code itself for
 * one nobody wrote, so a typo shows on screen rather than as a blank.
 */
export const fileLinkMessage = (code) => coreSync().fileLinkMessage(String(code)) ?? String(code);

export const fileLinkMessageCodes = () => coreSync().fileLinkMessageCodes();
